import { computed, ref } from 'vue'

import type { MediaImageProps } from '@fondof-ui/components/media/types'
import { contentfulImage } from '@internal/format-contentful'

const qualityMobile = 70
const qualityTablet = 70
const qualityDesktop = 70

export const useImage = (props: MediaImageProps) => {
  const imageLoaded = ref(false)

  const sizeMobile = computed(() => 640 / (props.dividerMobile || 1))
  const sizeTablet = computed(() => 960 / (props.dividerTablet || 1))
  const sizeDesktop = computed(() => 1440 / (props.dividerDesktop || 1))

  const mobileSrc = computed(
    () => props.imageMobile?.url || props.imageDesktop?.url
  )
  const tabletSrc = computed(
    () =>
      props.imageTablet?.url ||
      props.imageDesktop?.url ||
      props.imageMobile?.url
  )
  const desktopSrc = computed(
    () => props.imageDesktop?.url || props.imageMobile?.url
  )

  const isMobileSvg = computed(() => mobileSrc.value?.endsWith('.svg'))
  const isTabletSvg = computed(() => tabletSrc.value?.endsWith('.svg'))
  const isDesktopSvg = computed(() => desktopSrc.value?.endsWith('.svg'))

  const mobileFormat = computed(() => (isMobileSvg.value ? '' : '&fm=webp'))
  const tabletFormat = computed(() => (isTabletSvg.value ? '' : '&fm=webp'))
  const desktopFormat = computed(() => (isDesktopSvg.value ? '' : '&fm=webp'))

  const mobileSrcset = computed(() => {
    if (!mobileSrc.value) return ''
    return `${contentfulImage(mobileSrc.value, sizeMobile.value)}${mobileFormat.value}&q=${qualityMobile} 1x, ${contentfulImage(mobileSrc.value, sizeMobile.value * 2)}${mobileFormat.value}&q=${qualityMobile} 2x`
  })

  const tabletSrcset = computed(() => {
    if (!tabletSrc.value) return ''
    return `${contentfulImage(tabletSrc.value, sizeTablet.value)}${tabletFormat.value}&q=${qualityTablet} 1x, ${contentfulImage(tabletSrc.value, sizeTablet.value * 2)}${tabletFormat.value}&q=${qualityTablet} 2x`
  })

  const desktopSrcset = computed(() => {
    if (!desktopSrc.value) return ''
    return `${contentfulImage(desktopSrc.value, sizeDesktop.value)}${desktopFormat.value}&q=${qualityDesktop} 1x, ${contentfulImage(desktopSrc.value, sizeDesktop.value * 2)}${desktopFormat.value}&q=${qualityDesktop} 2x`
  })

  const mainImage = computed(
    () =>
      props.imageDesktop?.url ||
      props.imageTablet?.url ||
      props.imageMobile?.url
  )

  const imageQuality = computed(() => {
    if (props.imageDesktop?.url) return qualityDesktop
    if (props.imageTablet?.url) return qualityTablet
    return qualityMobile
  })

  const imageUrl = computed(() => {
    if (!mainImage.value) return ''
    return `${contentfulImage(mainImage.value)}&q=${imageQuality.value}${mainImage.value.endsWith('.svg') ? '' : '&fm=webp'}`
  })

  const isTabletAvailable = computed(() => !!props.imageTablet?.url)

  const imageAlt = computed(() => {
    if (typeof props.alt === 'string') {
      return props.alt
    }

    if (props.imageDesktop?.url && props.imageDesktop?.description) {
      return props.imageDesktop.description
    }

    if (props.imageTablet?.url && props.imageTablet?.description) {
      return props.imageTablet.description
    }

    if (props.imageMobile?.url && props.imageMobile?.description) {
      return props.imageMobile.description
    }

    return ''
  })

  return {
    imageLoaded,
    mobileSrc,
    tabletSrc,
    desktopSrc,
    mobileSrcset,
    tabletSrcset,
    desktopSrcset,
    imageUrl,
    isTabletAvailable,
    imageAlt,
  }
}
